import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _NumberUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { Assert } from "abweb/utils/Assert";
var NumberFormatOptions = Intl.NumberFormatOptions;
import { LogUtils } from "./log/LogUtils";
import { AbBigDecimal } from "./AbBigDecimal";
const LOGGER = LogUtils.getLogger(__filename);

// undefined => ""
// null  => ""
// "" => ""
// "abc" => "NaN"
// NaN => "NaN"
// Number.POSITIVE_INFINITY => "∞"
// "+Infinity" => "∞"
// "Infinity" => "∞"
// "-Infinity" => "-∞"
// 123 => "123"
// "123" => "123"

const validRegex = new RegExp(/^([-+]?((\d+\.?\d*)|(\d*\.?\d+))([eE][-+]?\d+)?)?$/); // Regex requires "." as decimal separator

const isValidString = s => {
  if (s === "Infinity" || s === "-Infinity" || s === "+Infinity") return true;
  return validRegex.test(s);
};
export class NumberUtils {
  constructor() {
    // static class, can't create an instance
  }

  /*
  A locale mapper can be set to handle situations where a locale that works for language and date formatting
  (which is performed by kendo culture) does not work for number formatting. A locale mapper must return the
  locale passed in or some other desired locale alternative that works for 'formatting'.  As an example,
  given 'en_FR' as the locale string, it would return 'fr' as the locale to use for numeric formatting.
   */

  static set formatLocaleMapper(formatLocaleMapper) {
    this._formatLocaleMapper = formatLocaleMapper;
    this.onLocaleChange(NumberUtils.formatLocale);
  }
  static resetStatics() {
    LocaleManager.registerAndInvokeSyncLocaleListener(localeState => NumberUtils.onLocaleChange(localeState.numberFormatLocale));
  }
  /**
   * Formats a number in the proper locale.
   * @param value Number to format
   * @param numDecimalPlaces Number of decimal places
   * If -1 output the number as is. This is the default behavior.
   * If 0, treat as an integer
   * If > 0, output that number of decimal places, rounding (not truncating) if needed.
   * @param use1000Separator true to include the locale-specific thousand separator, false to exclude it.
   * @param currency If non-null, this is the ISO 4217 currency code, eg, "USD", "EUR" to use. See
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat for more info
   */
  static formatValue(value, numDecimalPlaces = -1, use1000Separator = true, currency = null) {
    // Build formatting function (aligned with column renderers), and then use it for given value
    const numberFormatFunction = NumberUtils.buildNumberFormatter(numDecimalPlaces, use1000Separator, currency);
    return numberFormatFunction(value);
  }

  /**
   * Formats a percentage in the proper locale. The specified value is multiplied times 100 before formatting.
   * @param value Number to format. The value is multiplied times 100 before formatting. For example, 0.123 is formatted as "12.3%".
   * @param numDecimalPlaces Number of decimal places after multiplying by 100.
   * If -1, do not truncate nor extend the number of decimal places. This is the default behavior.
   * If 0, treat as an integer
   * If > 0, output that number of decimal places, rounding (not truncating) if needed.
   * @param use1000Separator true to include the locale-specific thousand separator, false to exclude it.
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat for more info
   */
  static formatPercentage(value, numDecimalPlaces = -1, use1000Separator = true) {
    const percentageFormatter = NumberUtils.buildPercentageNumberFormatter(numDecimalPlaces, use1000Separator);
    return percentageFormatter(value);
  }
  static buildPercentageNumberFormatter(numDecimalPlaces = -1, use1000Separator = true) {
    let minimumFractionDigits;
    let maximumFractionDigits;
    if (numDecimalPlaces < 0) {
      // toLocaleString() truncate the number to an integer, so override maximumFractionDigits to a large value.
      maximumFractionDigits = 20;
    } else if (numDecimalPlaces === 0) {
      maximumFractionDigits = 0;
    } else {
      minimumFractionDigits = numDecimalPlaces;
    }
    const formatter = new Intl.NumberFormat(NumberUtils.formatLocale, {
      style: "percent",
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: use1000Separator
    });
    return value => {
      return this.applyFormatter(value, formatter.format);
    };
  }
  static trimDecimalString(inString, numDecimalPlaces) {
    /* First, see if this is a numeric string, return otherwise */
    if (typeof inString !== "string" || !isValidString(inString)) {
      return inString;
    }
    /* Now, see if it's a decimal.  If not, return */
    let idx = inString.indexOf(".");
    if (idx === -1) {
      return inString;
    }
    if (numDecimalPlaces === undefined) {
      numDecimalPlaces = 5;
    }
    /* Now, see if there's more than the requested number of decimals */
    if (inString.length <= idx + numDecimalPlaces + 1) {
      return inString;
    }
    /* Now we have to truncate, and add horizontal ellipsis */
    return NumberUtils.formatValue(inString, numDecimalPlaces, false);
  }

  /** Return number formatter function that formats value according to provided formatting args.   */
  static buildNumberFormatter(numDecimalPlaces = -1, use1000Separator = true, currency = null, minimumDigits = 1, maximumSignificantDigits = -1, notation = undefined, compactDisplay = undefined) {
    if (numDecimalPlaces >= 0 || !!currency || !!notation)
      // not (undefined || null || "")
      {
        // Custom format
        const options = {
          useGrouping: use1000Separator
        };
        options.minimumIntegerDigits = minimumDigits;
        if (numDecimalPlaces >= 0) {
          options.minimumFractionDigits = options.maximumFractionDigits = numDecimalPlaces;
        } else {
          options.maximumFractionDigits = 20;
        }
        if (!!currency) {
          options.style = "currency";
          options.currency = currency;
        }
        if (maximumSignificantDigits > -1) {
          options.maximumSignificantDigits = maximumSignificantDigits;
        }
        if (notation !== undefined) {
          options.notation = notation;
        }
        if (compactDisplay !== undefined) {
          options.compactDisplay = compactDisplay;
        }
        const customFormatter = new Intl.NumberFormat(NumberUtils.formatLocale, options);
        return value => {
          return this.applyFormatter(value, customFormatter.format);
        };
      } else {
      // Format as is
      return use1000Separator ? value => {
        return this.applyFormatter(value, NumberUtils.defaultFormatter.format);
      } : value => {
        return this.applyFormatter(value, NumberUtils.defaultNoGroupingFormatter.format);
      };
    }
  }

  /**
   * Formats a number with a 1000s abbreviation (K/M/B)
   * e.g., 1234 = 1.234K
   * @param value
   * @param numDecimalPlaces
   * @param use1000Separator
   * @param currency
   * @param maximumSignificantDigits
   */
  static formatValue1000Abbr(value, numDecimalPlaces = -1, use1000Separator = true, currency = null, maximumSignificantDigits = -1) {
    // Build formatting function (aligned with column renderers), and then use it for given value
    const numberFormatFunction = NumberUtils.build1000AbbrNumberFormatter(numDecimalPlaces, use1000Separator, currency, maximumSignificantDigits);
    return numberFormatFunction(value);
  }

  // function uses formatValue1000Abbr to abbreviate number, but with extended logic to catch values over 1 trillion and to keep all abbreviations less than 4 characters total
  static extendedformatValue1000Abbr(value) {
    if (value >= 1000000000000) {
      return " 1T+";
    } else if (value < 10000 && value > 999) {
      return NumberUtils.formatValue1000Abbr(value, 1);
    } else {
      return NumberUtils.formatValue1000Abbr(value, 0);
    }
  }
  static build1000AbbrNumberFormatter(numDecimalPlaces = -1, use1000Separator = true, currency = null, maximumSignificantDigits = -1) {
    const numFormatter = NumberUtils.buildNumberFormatter(numDecimalPlaces, use1000Separator, currency, 1, maximumSignificantDigits, "compact", "short");
    return numFormatter;
  }
  static getLocaleString() {
    return this.formatLocale;
  }
  static build1024AbbrNumberFormatter(numDecimalPlaces = -1, use1000Separator = true) {
    const numFormatter = NumberUtils.buildNumberFormatter(numDecimalPlaces, use1000Separator, null);
    const formatter = value => {
      let bigDecimal = AbBigDecimal.from(value);
      if (bigDecimal.isNaN()) {
        LOGGER.error(`Could not convert '${value}' to number.`);
        return "";
      }
      if (bigDecimal.equals(0)) {
        return "0"; // special case
      }

      // Negative byte values probably don't make sense, but the goal here is to faithfully display data.
      const isNegative = bigDecimal.compare(0) === -1;
      if (isNegative) {
        bigDecimal = bigDecimal.multiply(-1n); // prepend "-" before returning below
      }

      // NOTE: Martha Hicks-Courant recently drew our attention to "Byte conventions" found in Ab Initio doc...
      /*
      All -byte terms refer to powers of 2 rather than powers of 10.
      Term	Abbreviation	Number of bytes
      kilobyte	kB	1,024(2 to the 10th power)
      megabyte	MB	1,048,576 (2 to the 20th power)
      gigabyte	GB	1,073,741,824 (2 to the 30th power)
      terabyte	TB	1,099,511,627,776 (2 to the 40th power)
      */
      const abbreviations = [
      // @see https://en.wikipedia.org/wiki/File_size
      "",
      // or "B" ?
      "kB", "MB", "GB", "TB",
      // Terabyte
      "PB",
      // Petabyte
      "EB",
      // Exabyte
      "ZB",
      // Zettabyte
      "YB",
      // Yottabyte
      "WholeLottaBytes" // Lottabyte
      ];
      for (let index = abbreviations.length - 1; index >= 0; index--) {
        const log2Threshold = 10 * index;
        const dividedValue = bigDecimal.divide(log2Threshold, 0);
        if (dividedValue.compare(1) !== -1) {
          const formatted = numFormatter(dividedValue.toString());
          return (isNegative ? "-" : "") + formatted + abbreviations[index];
        }
      }
      Assert.assert(false, value + ": unknown value"); // "can't happen"
    };
    return value => this.applyFormatter(value, formatter);
  }

  /**
   * Tests equality of 2 numbers.
   * This method treats value1 = value2 = NaN as true.
   * @param value1
   * @param value2
   * @returns {boolean}
   */
  static areEqual(value1, value2) {
    if (value1 == null || value2 == null) {
      return value1 === value2;
    }
    if (isNaN(value1) && isNaN(value2)) {
      return true;
    }
    return value1 === value2;
  }

  /**
   * Gets the current locale's decimal separator character.
   * @returns {string}
   */
  static getLocaleDecimalSeparator() {
    return NumberUtils.decimalSeparator;
  }
  static getLocaleGroupingSeparator() {
    return NumberUtils.groupingSeparator;
  }

  /**
   * This method is a locale-aware variant of javascript parseFloat.
   * @return number parsed out of string that can contain locale-specific decimal separator
   * and locale-specific separators.  The returned number will be NaN when parsing fails.
   */
  static parseLocaleFloat(value) {
    return NumberUtils.parseLocaleNumber(value, true);
  }

  /**
   * This method is a locale-aware variant of javascript parseInt.
   * @return int parsed out of string that can contain locale-specific decimal separator
   * and locale-specific separators.  The returned number will be NaN when parsing fails.
   */
  static parseLocaleInt(value) {
    return NumberUtils.parseLocaleNumber(value, false);
  }
  static parseLocaleNumber(value, isFloat) {
    const decimalSep = NumberUtils.getLocaleDecimalSeparator();
    if (decimalSep !== ".") {
      // Convert decimal separator to canonical '.'
      value = value.replace(decimalSep, ".");
    }
    const groupSep = NumberUtils.getLocaleGroupingSeparator();
    if (groupSep) {
      // Remove all group seps
      value = value.replaceAll(groupSep, "");
    }
    return isFloat ? parseFloat(value) : parseInt(value);
  }

  /**
   * Returns true if integer is an even number
   * @param integer
   * @returns {boolean}
   */
  static isEven(integer) {
    if (Number.isInteger(integer)) {
      return integer % 2 === 0;
    } else {
      throw new Error("The value is not an integer");
    }
  }

  /**
   * returns true if integer is an odd number
   * @param integer
   * @returns {boolean}
   */
  static isOdd(integer) {
    return !NumberUtils.isEven(integer);
  }

  /**
   * Add numbers together, interpreting null and undefined as zero.
   */
  static add(a, b) {
    return (a ?? 0) + (b ?? 0);
  }

  /**
   * Apply a simple constraint function with optional lower and upper bounds to a number.
   * Behavior is not defined if upperBound is less than lowerBound.
   */
  static clamp(value, lowerBound, upperBound) {
    const lowerApplied = Math.max(value, lowerBound ?? value);
    return Math.min(lowerApplied, upperBound ?? lowerApplied);
  }

  /**
   * Checks if value is in the range [lowerBound, upperBound], inclusive. Undefined lowerBound and upperBound are
   * considered nonrestrictive.
   */
  static inRange(value, lowerBound, upperBound) {
    return (lowerBound ?? Number.MIN_VALUE) <= value && value <= (upperBound ?? Number.MAX_VALUE);
  }

  /**
   * Checks if x is within tolerance of y.
   */

  /**
   * Truncates a decimal value without rounding.
   * @param {number} value The value to truncate.
   * @param {number} numDecimalPlaces The number of decimal places to retain.
   * @returns {number}
   */
  static truncate(value, numDecimalPlaces) {
    const bigDecimal = AbBigDecimal.from(value);
    Assert.assert(bigDecimal.isFinite(), `invalid value passed to truncate(): '${value}'`);
    return bigDecimal.truncate(numDecimalPlaces).numberValue;
  }
  static compareNumbers(num1, num2) {
    let result = 0;
    if (num1 && !num2) {
      result = 1;
    } else if (!num1 && num2) {
      result = -1;
    } else if (num1 !== num2) {
      result = num1 > num2 ? 1 : -1;
    }
    return result;
  }
  static isNumber(value) {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }
  static onLocaleChange(locale) {
    NumberUtils.formatLocale = NumberUtils._formatLocaleMapper ? NumberUtils._formatLocaleMapper(locale) : locale;
    NumberUtils.defaultFormatter = new Intl.NumberFormat(NumberUtils.formatLocale, {
      maximumFractionDigits: 20
    });
    NumberUtils.defaultNoGroupingFormatter = new Intl.NumberFormat(NumberUtils.formatLocale, {
      maximumFractionDigits: 20,
      useGrouping: false
    });
    // Calc locale-specific decimal and grouping separators
    [NumberUtils.decimalSeparator, NumberUtils.groupingSeparator] = NumberUtils.calcLocaleDecimalAndGroupingSeparators();
  }

  /** @return locale-specific decimal and grouping separators for current locale */
  static calcLocaleDecimalAndGroupingSeparators() {
    const formatter = new Intl.NumberFormat(NumberUtils.formatLocale);
    // Use a number that will clearly show a grouping separator
    const parts = formatter.formatToParts(10000.2);
    const decimalPart = parts.find(part => part.type === 'decimal');
    const decimalSep = decimalPart?.value;
    Assert.assert(!!decimalSep, "The decimal separator could not be calculated");
    const groupPart = parts.find(part => part.type === 'group');
    const groupSep = groupPart?.value ?? ""; // groupSep can be empty string!
    return [decimalSep, groupSep];
  }
}

// Outside of class to guarantee that it is 'last' (even when intellij reorders static class members!)
// noinspection JSUnusedLocalSymbols
_NumberUtils = NumberUtils;
// See TSLanguageSuite:"Performance of Intl.Number formatting" test where show that overhead of
// creating Intl.NumberFormatter is negligible.  So the build*NumberFormatter PUBLIC apis are
// really over-engineered (negligible performance savings to creating formatter once and using it 10000 times
// compared to creating and using it 10000 times)
/**
 * Locale-specific integers as grouping char, and grouping size are controlled by the LOCALE.
 *
 * It outputs the locale-specific grouping separator.
 */
_defineProperty(NumberUtils, "FORMAT_INTEGER", _NumberUtils.buildNumberFormatter(0, true, null));
// This is the default formatter used when numDecimalPlaces is not present.
// A note on the MDN doc page suggests that when formatting lots of numbers, this is a more efficient way to go:
_defineProperty(NumberUtils, "FORMAT_INTEGER2", _NumberUtils.buildNumberFormatter(0, false, null, 2));
_defineProperty(NumberUtils, "FORMAT_INTEGER4", _NumberUtils.buildNumberFormatter(0, false, null, 4));
/**
 * Locale-specific as decimal char, grouping char, and grouping size are controlled by the LOCALE.
 *
 * Output at most 2 decimal places.
 */
_defineProperty(NumberUtils, "FORMAT_DECIMAL_2", _NumberUtils.buildNumberFormatter(2, true, null));
/**
 * Locale-specific as decimal char, grouping char, and grouping size are controlled by the LOCALE.
 *
 * Output at most 3 decimal places.
 */
_defineProperty(NumberUtils, "FORMAT_DECIMAL_3", _NumberUtils.buildNumberFormatter(3, true, null));
/**
 * Locale-specific as decimal char, grouping char, and grouping size are controlled by the LOCALE.
 *
 * Output at most 20 decimal places and is the default formatter.
 */
_defineProperty(NumberUtils, "FORMAT_DECIMAL_20", _NumberUtils.buildNumberFormatter(-1, true, null));
// NB: undefined means use browser locale
_defineProperty(NumberUtils, "formatLocale", void 0);
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
_defineProperty(NumberUtils, "defaultFormatter", void 0);
_defineProperty(NumberUtils, "defaultNoGroupingFormatter", void 0);
// locale-specific decimal separator used for parsing numbers (see parseLocaleFloat())
_defineProperty(NumberUtils, "decimalSeparator", undefined);
// onLocaleChange initialized
// locale-specific grouping separator used for parsing numbers (see parseLocaleFloat())
_defineProperty(NumberUtils, "groupingSeparator", undefined);
_defineProperty(NumberUtils, "_formatLocaleMapper", void 0);
_defineProperty(NumberUtils, "applyFormatter", (input, formatter) => {
  if (input === undefined || input === null || typeof input === "string" && input.length === 0) return "";
  const formattedValue = formatter(input);
  if (formattedValue.includes(Number.NaN.toString())) LOGGER.error(`Unable to format ${input} to a number, rendering it as '${formattedValue}'`);
  return formattedValue;
});
_defineProperty(NumberUtils, "fuzzyEquals", (x, y, tolerance) => {
  return Math.abs(x - y) <= tolerance;
});
NumberUtils.resetStatics();
//# sourceMappingURL=NumberUtils.js.map
